import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { flightService } from '../../services/flightService';
import bookingService from '../../services/bookingService';
import UniversalConfirmModal from '../../components/UniversalConfirmModal/UniversalConfirmModal';
import {
  mergeBookingData,
  navigateWithBookingData,
  serializeBookingData
} from '../../utils/bookingQueryParams';
import apiClient from '../../services/apiClient';
import { useSessionExpiration } from '../../hooks/useSessionExpiration';
import { showSessionExpiredNotice } from '../../utils/sessionExpiredNotice';
import SessionExpiredModal from '../../components/Auth/SessionExpiredModal';
import SessionTimeoutWarningModal from '../../components/Auth/SessionTimeoutWarningModal';
import { useCurrencyDisplay } from '../../hooks/useCurrencyDisplay';
import {
  calculateFlightFarePerPax,
  calculateFlightLegTotal,
  calculateFlightPricing,
  formatFlightDurationLabel
} from '../../utils/flightPricing';
import {
  BOOKING_HISTORY_PATH,
  buildCancelledFlightMessage,
  collectFlightsFromPaymentState,
  findCancelledFlights,
  isFlightCancelledApiResponse,
} from '../../utils/paymentFlightGuard';

const isReturnRoute = (type) =>
  ['Return', 'Returns', 'return', 'round_trip', 'Round Trip'].includes(type || '');

const isMultiCityRoute = (type) => type === 'Multicity';

/** Multicity legs from search/URL only have `price`; API details include seat_price/block_price */
const getMultiCityFlightFarePerPax = (flight) => {
  const fare = calculateFlightFarePerPax(flight);
  if (fare > 0) return fare;
  if (flight?.price != null && !Number.isNaN(Number(flight.price))) {
    return Number(flight.price);
  }
  return 0;
};

const getMultiCityFlightLegTotal = (flight, passengerCount) => {
  const total = calculateFlightLegTotal(flight, passengerCount);
  if (total > 0) return total;
  const farePerPax = getMultiCityFlightFarePerPax(flight);
  if (farePerPax <= 0) return 0;
  return flight?.is_bel_flight ? farePerPax : farePerPax * passengerCount;
};

const normalizePaymentMethod = (value) =>
  value === 'stripe' || value === 'paypal' ? value : '';

const mapFlightToAircraftPayload = (flight) => ({
  routes_id: flight.id,
  aircraft_id: flight.aircraft_id,
  registration_number: flight.registration_number,
  flight_number: flight.flight_number,
  departure_airport: flight.departure_airport,
  departure_airport_iata: flight.departure?.airport_code || flight.departure_airport_iata,
  departure_airport_name: flight.departure?.airport_name || flight.departure_airport_name,
  departure_date_time: flight.departure?.datetime || flight.departure_date_time,
  arrival_airport: flight.arrival_airport,
  arrival_airport_iata: flight.arrival?.airport_code || flight.arrival_airport_iata,
  arrival_airport_name: flight.arrival?.airport_name || flight.arrival_airport_name,
  arrival_date_time: flight.arrival?.datetime || flight.arrival_date_time,
  seat_price: flight.seat_price || flight.block_empty_price,
});

/** When only some legs are being changed, lock/pay for those legs only. */
const filterAircraftDataForPartialChange = (
  fullAircraftData,
  changeLegsQuery,
  originalFlights,
  routeType
) => {
  const changeTokens = (changeLegsQuery || '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);

  if (
    !changeTokens.length ||
    !originalFlights?.length ||
    changeTokens.length >= originalFlights.length ||
    !fullAircraftData?.length
  ) {
    return fullAircraftData;
  }

  const changeSet = new Set(changeTokens);
  const sortedOriginal = [...originalFlights].sort(
    (a, b) =>
      new Date(a.departure?.datetime || a.departure_date_time).getTime() -
      new Date(b.departure?.datetime || b.departure_date_time).getTime()
  );

  const result = [];
  sortedOriginal.forEach((leg, index) => {
    if (!changeSet.has(leg.booking_token)) return;
    if (fullAircraftData[index]) {
      result.push(fullAircraftData[index]);
    } else if (routeType === 'return' && fullAircraftData.length >= 2) {
      result.push(fullAircraftData[index] ?? fullAircraftData[fullAircraftData.length - 1]);
    } else {
      result.push(fullAircraftData[0]);
    }
  });

  return result.length > 0 ? result : fullAircraftData;
};

/** Sum new itinerary fare only for legs the user is changing (matches filterAircraftDataForPartialChange). */
const calculateNewFareForChangedLegs = ({
  changeLegsQuery,
  originalFlights,
  flightData,
  returnFlightData,
  multiCityFlights,
  totalPassengers,
  isMultiCity,
  isReturnTrip,
}) => {
  const fullTripNewFare = () => {
    if (isMultiCity && multiCityFlights?.length > 0) {
      return multiCityFlights.reduce(
        (sum, flight) => sum + getMultiCityFlightLegTotal(flight, totalPassengers),
        0
      );
    }
    if (isReturnTrip && returnFlightData) {
      return (
        calculateFlightLegTotal(flightData, totalPassengers) +
        calculateFlightLegTotal(returnFlightData, totalPassengers)
      );
    }
    return calculateFlightLegTotal(flightData, totalPassengers);
  };

  if (!flightData) return 0;

  const changeTokens = (changeLegsQuery || '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);

  if (
    !changeTokens.length ||
    !originalFlights?.length ||
    changeTokens.length >= originalFlights.length
  ) {
    return fullTripNewFare();
  }

  const changeSet = new Set(changeTokens);
  const sortedOriginal = [...originalFlights].sort(
    (a, b) =>
      new Date(a.departure?.datetime || a.departure_date_time).getTime() -
      new Date(b.departure?.datetime || b.departure_date_time).getTime()
  );

  let total = 0;
  sortedOriginal.forEach((leg, index) => {
    if (!changeSet.has(leg.booking_token)) return;
    if (isMultiCity && multiCityFlights?.[index]) {
      total += getMultiCityFlightLegTotal(multiCityFlights[index], totalPassengers);
    } else if (isReturnTrip && returnFlightData) {
      if (index === 0) {
        total += calculateFlightLegTotal(flightData, totalPassengers);
      } else if (index === 1) {
        total += calculateFlightLegTotal(returnFlightData, totalPassengers);
      }
    } else {
      total += calculateFlightLegTotal(flightData, totalPassengers);
    }
  });

  return total > 0 ? total : fullTripNewFare();
};

// Helper function to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '/assets/images/no_aircraft.png';

  // If it's already a full URL (starts with http:// or https://), return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // If it's a relative path, prepend the API base URL (without /api/v1)
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
  const baseUrl = apiBaseUrl.replace('/api/v1', '');
  return `${baseUrl}/${imageUrl}`;
};

const PaymentPage = ({ onBack }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth);

  // Merge data from URL params, location state, and session storage
  const bookingData = mergeBookingData(location);

  const [flightData, setFlightData] = useState(null);
  const [returnFlightData, setReturnFlightData] = useState(null);
  const [multiCityFlights, setMultiCityFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [address, setAddress] = useState('');
  const [addressError, setAddressError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(() =>
    normalizePaymentMethod(bookingData.payment_method)
  );
  const [promoCode, setPromoCode] = useState(bookingData.promo_code || '');
  const [availablePromos, setAvailablePromos] = useState([]);
  const [promosLoading, setPromosLoading] = useState(false);
  const [promosEnabled, setPromosEnabled] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [originalBooking, setOriginalBooking] = useState(null);
  const [bookingToken, setBookingToken] = useState(bookingData.booking_token || null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showTimer, setShowTimer] = useState(false);
  const [noPaymentRequired, setNoPaymentRequired] = useState(false);
  const [changeFeeBreakdown, setChangeFeeBreakdown] = useState(null); // tiered fee breakdown for rebooking
  const [showSessionTimeoutWarning, setShowSessionTimeoutWarning] = useState(false);
  const [sessionValidity, setSessionValidity] = useState(null);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState(null);
  const skipSessionCheckRef = useRef(false); // Use ref for synchronous updates
  const timerIntervalRef = useRef(null);
  
  // Session expiration handling
  const { showSessionModal, handleLoginSuccess, handleCloseModal } = useSessionExpiration();
  
  // Currency display hook
  const { getFormattedWithBoth, isLocalCurrency, getDisclaimer, getPrices } = useCurrencyDisplay();
  
  const passengers = bookingData.passengerData || [];
  const totalPassengers = bookingData.passengers || 1;
  const routeType = bookingData.routeType || 'One Way';
  const returnFlightId = bookingData.return_flight_id;
  const isReturnTrip =
    isReturnRoute(routeType) ||
    Boolean(returnFlightId) ||
    (routeType !== 'Multicity' && (bookingData.selectedFlights?.length || 0) > 1);
  const isMultiCity = routeType === 'Multicity' && bookingData.selectedFlights && bookingData.selectedFlights.length > 1;
  const pricing = useMemo(
    () =>
      bookingData.pricing || {
        farePerPax: bookingData.farePerPax,
        totalFare: bookingData.totalFare,
        blockFare: bookingData.blockFare,
      },
    [bookingData.pricing, bookingData.farePerPax, bookingData.totalFare, bookingData.blockFare]
  );
  const changeLegsQuery = searchParams.get('change_legs') || '';
  const lastChangeFeeRequestKeyRef = useRef('');
  // Initialize address from booking data or user profile
  useEffect(() => {
    // First check for auto-saved address
    const addressDataKey = `payment_address_data_${id}`;
    const savedAddressData = sessionStorage.getItem(addressDataKey);
    
    if (savedAddressData) {
      try {
        const parsed = JSON.parse(savedAddressData);
        // Check if data is recent (within 30 minutes)
        const thirtyMinutes = 30 * 60 * 1000;
        if (Date.now() - parsed.timestamp < thirtyMinutes) {
          console.log('✅ Restoring auto-saved address from sessionStorage');
          setAddress(parsed.address);
          return;
        } else {
          console.log('⚠️ Auto-saved address expired, clearing...');
          sessionStorage.removeItem(addressDataKey);
        }
      } catch (error) {
        console.error('❌ Failed to parse saved address:', error);
        sessionStorage.removeItem(addressDataKey);
      }
    }
    
    // Otherwise use booking data or user profile
    setAddress(bookingData.address || user?.address || '');
  }, [bookingData.address, user?.address, id]);

  // Auto-save address to sessionStorage
  useEffect(() => {
    if (address && address.trim() !== '') {
      const addressDataKey = `payment_address_data_${id}`;
      sessionStorage.setItem(addressDataKey, JSON.stringify({
        address,
        timestamp: Date.now()
      }));
      console.log('💾 Auto-saved address to sessionStorage');
    }
  }, [address, id]);

  // Keep payment method in sync when URL query params change (e.g. back from passengers step)
  useEffect(() => {
    const fromUrl = normalizePaymentMethod(bookingData.payment_method);
    if (fromUrl) {
      setPaymentMethod(fromUrl);
    }
  }, [bookingData.payment_method]);

  useEffect(() => {
    if (bookingData.promo_code !== undefined && bookingData.promo_code !== promoCode) {
      setPromoCode(bookingData.promo_code || '');
    }
  }, [bookingData.promo_code]);

  const getPayableAmount = () => {
    if (!flightData) return 0;
    const pricingData = calculatePricing();
    let totalAmount = 0;
    if (pricingData.blockFare && pricingData.blockFare > 0) {
      totalAmount = pricingData.blockFare;
    } else if (pricingData.totalFare && pricingData.totalFare > 0) {
      totalAmount = pricingData.totalFare;
    }
    if (
      originalBooking &&
      pricingData.payableDifference !== undefined &&
      pricingData.payableDifference !== null
    ) {
      totalAmount = Number(pricingData.payableDifference);
    }
    return totalAmount;
  };

  useEffect(() => {
    if (!showPaymentModal || !flightData || paymentMethod !== 'stripe') {
      return undefined;
    }

    let cancelled = false;

    const loadPromos = async () => {
      setPromosLoading(true);
      try {
        const paymentService = (await import('../../services/paymentService')).default;
        const amount = getPayableAmount();
        const response = await paymentService.listPromotionCodes(
          amount > 0 ? amount : undefined
        );
        const payload = response?.data ?? response;
        if (!cancelled) {
          setAvailablePromos(payload?.codes || []);
          setPromosEnabled(payload?.enabled !== false);
        }
      } catch (err) {
        console.warn('Failed to load promotion codes:', err);
        if (!cancelled) {
          setAvailablePromos([]);
        }
      } finally {
        if (!cancelled) {
          setPromosLoading(false);
        }
      }
    };

    loadPromos();
    return () => {
      cancelled = true;
    };
  }, [
    showPaymentModal,
    flightData?.id,
    paymentMethod,
    originalBooking?.booking_token,
    changeFeeBreakdown?.priceBreakdown?.totalOwed,
    pricing.totalFare,
    pricing.blockFare,
    totalPassengers,
  ]);

  const formatPromoExpiry = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return null;
    }
  };

  const handleSelectPromoCode = async (promo) => {
    if (!promo?.eligible) {
      toast.error(promo?.ineligible_reason || 'This code cannot be used for this booking');
      return;
    }

    setPromoCode(promo.code);
    syncBookingDataToUrl({ promo_code: promo.code });

    if (paymentMethod === 'stripe' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(promo.code);
        toast.success(`Copied ${promo.code} — paste it on the Stripe checkout page`);
      } catch {
        toast.success(`Use code ${promo.code} on the Stripe checkout page`);
      }
    } else {
      toast.success(`Use code ${promo.code} on the Stripe checkout page`);
    }
  };

  const renderAvailablePromoCodes = () => {
    if (!promosEnabled) {
      return (
        <p className="text-xs text-gray-500 text-center m-0 mb-2">
          Promotion codes are disabled for this environment.
        </p>
      );
    }

    if (promosLoading) {
      return (
        <p className="text-xs text-gray-500 text-center m-0 mb-2">Loading available offers…</p>
      );
    }

    if (!availablePromos.length) {
      return (
        <p className="text-xs text-gray-500 text-center m-0 mb-2">
          No promotion codes are configured in Stripe (test mode). Create codes in the Stripe Dashboard
          or set BOOKING_PROMO_CODES_JSON on the server for local testing.
        </p>
      );
    }

    return (
      <div className="mb-3">
        <p className="text-sm font-semibold text-gray-800 m-0 mb-2">Available promotion codes</p>
        <ul className="list-none m-0 p-0 space-y-2 max-h-36 overflow-y-auto">
          {availablePromos.map((promo) => {
            const expiry = formatPromoExpiry(promo.expires_at);
            const isSelected =
              promoCode.trim().toUpperCase() === promo.code.trim().toUpperCase();
            return (
              <li key={promo.code}>
                <button
                  type="button"
                  className={`w-full text-left border rounded-lg px-3 py-2 text-sm transition-colors ${
                    promo.eligible
                      ? isSelected
                        ? 'border-yellow-500 bg-yellow-50'
                        : 'border-gray-200 hover:border-yellow-400 hover:bg-gray-50'
                      : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                  }`}
                  onClick={() => handleSelectPromoCode(promo)}
                  disabled={!promo.eligible || processing}
                  title={promo.ineligible_reason || undefined}
                >
                  <span className="font-bold text-gray-900 tracking-wide">{promo.code}</span>
                  <span className="text-gray-600"> — {promo.description}</span>
                  {promo.minimum_amount_usd != null && (
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Min. order ${Number(promo.minimum_amount_usd).toFixed(2)}
                    </span>
                  )}
                  {expiry && (
                    <span className="block text-xs text-gray-500">Expires {expiry}</span>
                  )}
                  {!promo.eligible && promo.ineligible_reason && (
                    <span className="block text-xs text-red-600 mt-0.5">{promo.ineligible_reason}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const syncBookingDataToUrl = (overrides = {}) => {
    const updated = {
      ...bookingData,
      address,
      ...(paymentMethod ? { payment_method: paymentMethod } : {}),
      ...(promoCode.trim() ? { promo_code: promoCode.trim() } : {}),
      ...overrides
    };
    if (!updated.payment_method) {
      delete updated.payment_method;
    }
    if (!updated.promo_code) {
      delete updated.promo_code;
    }
    const queryString = serializeBookingData(updated);
    navigate(`${location.pathname}?${queryString}`, {
      replace: true,
      state: updated
    });
    return updated;
  };

  const selectPaymentMethod = (method) => {
    setPaymentMethod(method);
    if (method === 'paypal') {
      setPromoCode('');
      syncBookingDataToUrl({ payment_method: method, promo_code: '' });
      return;
    }
    syncBookingDataToUrl({ payment_method: method });
  };

  // Handle payment cancellation (user clicked Back on Stripe/PayPal checkout)
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      toast.error('Payment was cancelled. You can try again.');
      
      // ✅ NEW: Revoke seat locks
      const bookingToken = bookingData.booking_token || user?.change_flight;
      if (bookingToken) {
        bookingService.revokeLock(bookingToken)
          .then(() => console.log('✅ [PaymentPage] Seat lock revoked successfully'))
          .catch(err => console.warn('⚠️ [PaymentPage] Failed to revoke lock:', err));
      }
      
      // Clear change_flight on backend so next booking is treated as new
      apiClient.post('/payments/clear-change-flight')
        .then(() => console.log('✅ [PaymentPage] change_flight cleared'))
        .catch(err => console.warn('⚠️ [PaymentPage] Failed to clear change_flight:', err));
      
      // Clean up the URL param without re-rendering
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams, navigate, location.pathname, bookingData.booking_token, user?.change_flight]);

  // Timer effect for payment
  useEffect(() => {
    if (showTimer && !timeRemaining) {
      // Start 10-minute timer (600 seconds) — matches seat lock and payment session expiry
      setTimeRemaining(600);
    }

    if (timeRemaining !== null && timeRemaining > 0 && showTimer) {
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            // Timer expired - show error and reset
            toast.error('Payment session expired. Please try again.');
            setShowTimer(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [showTimer, timeRemaining]);

  // Format time for display (MM:SS)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get timer color based on remaining time
  const getTimerColor = () => {
    if (!timeRemaining) return '#000';
    if (timeRemaining <= 60) return '#dc3545'; // Red if less than 1 minute
    if (timeRemaining <= 300) return '#ff9800'; // Orange if less than 5 minutes
    return '#000'; // Black otherwise
  };

  useEffect(() => {
    // ✅ FIRST: Load rebooking data (ONLY if it's a flight change)
    const loadRebookingData = async () => {
      // Only load rebooking data if we have a booking_token (flight change flow)
      const rebookingToken = bookingData.booking_token;
      
      if (!rebookingToken) {
        console.log('ℹ️ [PaymentPage] No booking_token - this is a regular new booking, skipping rebooking data load');
        return;
      }

      let currentChangeFlight = user?.change_flight;

      // If we are logged in but don't see change_flight in Redux, it might be stale
      if (user && !currentChangeFlight) {
        try {
          console.log('🔄 [PaymentPage] change_flight missing in Redux, fetching fresh profile...');
          const profileRes = await apiClient.get('/auth/profile');
          if (profileRes.success && profileRes.data.change_flight) {
            currentChangeFlight = profileRes.data.change_flight;
            console.log('✅ [PaymentPage] Found fresh change_flight token:', currentChangeFlight);
          }
        } catch (err) {
          console.warn('⚠️ [PaymentPage] Failed to fetch fresh profile:', err);
        }
      }

      // Prefer backend session token for rebooking context.
      // URL booking_token may be a pending/new lock token after refresh.
      const finalToken = currentChangeFlight || rebookingToken;
      if (finalToken) {
        console.log('🔄 [PaymentPage] Attempting to fetch original booking with token:', finalToken);
        fetchOriginalBooking(finalToken);
      }
    };

    loadRebookingData();

    // ✅ SECOND: Check passengers
    if (!passengers || passengers.length === 0) {
      console.log('❌ No passengers found, redirecting to add-passengers page');
      navigateWithBookingData(navigate, `/add-passengers/${id}`, bookingData);
      return;
    }

    console.log('✅ Passengers found, fetching flight details');
    fetchFlightDetails();
  }, [id, user?.id]);

  const fetchOriginalBooking = async (token) => {
    const searchToken = token || user?.change_flight;
    if (!searchToken) return;

    try {
      console.log('🔍 [PaymentPage] Fetching original booking details for:', searchToken);
      const response = await bookingService.getBookingByReference(searchToken);
      if (response.success) {
        console.log('✅ [PaymentPage] Original booking loaded:', response.data.reference_number);
        setOriginalBooking(response.data);
        // Note: Change fee breakdown will be fetched in useEffect once flightData is available
      } else {
        console.warn('⚠️ [PaymentPage] Failed to load original booking:', response.message);
      }
    } catch (err) {
      console.error('❌ [PaymentPage] Error loading original booking:', err);
    }
  };

  // Re-fetch change fee breakdown once flightData is available so new_amount is accurate
  useEffect(() => {
    if (!originalBooking || !flightData) return;
    const searchToken = bookingData.booking_token || user?.change_flight;
    if (!searchToken) return;

    const originalFlightsForChange =
      originalBooking?.flights ||
      (Array.isArray(originalBooking?.formattedBookings)
        ? originalBooking.formattedBookings
        : null);

    const totalNewFare = calculateNewFareForChangedLegs({
      changeLegsQuery,
      originalFlights: originalFlightsForChange,
      flightData,
      returnFlightData,
      multiCityFlights,
      totalPassengers,
      isMultiCity,
      isReturnTrip,
    });

    if (!totalNewFare || totalNewFare <= 0) return;

    const requestKey = `${searchToken}:${totalNewFare}:${changeLegsQuery}`;
    if (lastChangeFeeRequestKeyRef.current === requestKey) return;
    lastChangeFeeRequestKeyRef.current = requestKey;

    console.log('💰 [PaymentPage] Calculating change fee breakdown with total new fare:', totalNewFare);

    apiClient.get(
      `/bookings/${searchToken}/change-fee-breakdown?new_amount=${totalNewFare}${
        changeLegsQuery ? `&change_legs=${encodeURIComponent(changeLegsQuery)}` : ''
      }`
    )
      .then(res => {
        if (res.success && res.data) {
          console.log('✅ [PaymentPage] Change fee breakdown refreshed with new fare:', res.data);
          setChangeFeeBreakdown(res.data);
        }
      })
      .catch(err => {
        lastChangeFeeRequestKeyRef.current = '';
        console.warn('⚠️ [PaymentPage] Could not refresh change fee breakdown:', err);
      });
  }, [
    originalBooking?.booking_token,
    flightData?.id,
    returnFlightData?.id,
    multiCityFlights?.length,
    changeLegsQuery,
    bookingData.booking_token,
    user?.change_flight,
    isMultiCity,
    isReturnTrip,
    totalPassengers,
  ]);

  // ✅ NEW: Refresh pending booking expiration when showing free booking button
  useEffect(() => {
    if (!noPaymentRequired || !bookingToken) return;
    
    console.log('🔄 [PaymentPage] Free booking button shown, refreshing pending booking expiration');
    
    const refreshPendingBooking = async () => {
      try {
        await apiClient.post('/bookings/refresh-pending', {
          booking_token: bookingToken
        });
        console.log('✅ [PaymentPage] Refreshed pending booking expiration for token:', bookingToken);
      } catch (err) {
        console.warn('⚠️ [PaymentPage] Could not refresh pending booking:', err);
        // Continue anyway — user can still proceed
      }
    };
    
    refreshPendingBooking();
  }, [noPaymentRequired, bookingToken]);

  // IMPORTANT: Do not clear change_flight on beforeunload.
  // Browser refresh also triggers beforeunload, which incorrectly wipes rebooking context.
  // Explicit user actions (Back/cancel/payment callbacks) already clear this session.

  const fetchFlightDetails = async () => {
    let loadedFlights = [];

    try {
      setLoading(true);

      if (isMultiCityRoute(routeType) && bookingData.selectedFlights?.length) {
        const hasApiPricingData = bookingData.selectedFlights.every(
          (f) => f.seat_price != null && f.block_price != null
        );

        if (hasApiPricingData) {
          loadedFlights = bookingData.selectedFlights;
          setMultiCityFlights(bookingData.selectedFlights);
          setFlightData(bookingData.selectedFlights[0]);
          return;
        }

        const flightIds = bookingData.selectedFlights.map((f) => f.id).filter(Boolean);
        if (flightIds.length === 0) {
          setError('No flights selected');
          return;
        }

        const response = await flightService.getMultipleFlightDetails({
          flight_ids: flightIds
        });

        if (response.success && response.data) {
          const flightList = Array.isArray(response.data) ? response.data : [];
          const ordered = flightIds
            .map((fid) => flightList.find((f) => f.id === fid))
            .filter(Boolean);
          const flights = ordered.length > 0 ? ordered : flightList;
          loadedFlights = flights;
          setMultiCityFlights(flights);
          setFlightData(flights[0]);
        } else {
          setError('Flights not found');
        }
        return;
      }

      if (isReturnTrip) {
        const outboundId = parseInt(id, 10);
        const returnId = parseInt(
          returnFlightId ||
            bookingData.selectedFlights?.[1]?.id ||
            bookingData.selectedFlights?.[1]?.flight_id,
          10
        );

        if (!outboundId || !returnId || Number.isNaN(returnId)) {
          setError('Return flight not found');
          return;
        }

        const response = await flightService.getMultipleFlightDetails({
          outbound_flight_id: outboundId,
          return_flight_id: returnId
        });

        if (response.success) {
          loadedFlights = [response.data.outbound, response.data.return].filter(Boolean);
          setFlightData(response.data.outbound);
          setReturnFlightData(response.data.return);
        } else {
          setError('Flights not found');
        }
        return;
      }

      const response = await flightService.getFlightDetails(id);

      if (response.success) {
        loadedFlights = [response.data];
        setFlightData(response.data);
        setReturnFlightData(null);
      } else {
        setError('Flight not found');
      }
    } catch (err) {
      console.error('❌ Failed to load flight details:', err);
      setError('Failed to load flight details');
    } finally {
      if (loadedFlights.length > 0) {
        const cancelled = findCancelledFlights(loadedFlights);
        if (cancelled.length > 0) {
          toast.error(buildCancelledFlightMessage(cancelled), { duration: 8000 });
          navigate(BOOKING_HISTORY_PATH, { replace: true });
        } else {
          const routeIds = loadedFlights.map((f) => f.id).filter(Boolean);
          if (routeIds.length > 0) {
            try {
              const paymentService = (await import('../../services/paymentService')).default;
              const verifyRes = await paymentService.verifyFlightsForPayment({ route_ids: routeIds });
              if (!verifyRes.success && isFlightCancelledApiResponse(verifyRes)) {
                toast.error(verifyRes.message || buildCancelledFlightMessage([]), { duration: 8000 });
                navigate(BOOKING_HISTORY_PATH, { replace: true });
              }
            } catch (verifyErr) {
              const payload = verifyErr.data || verifyErr;
              if (isFlightCancelledApiResponse(payload)) {
                toast.error(payload.message || buildCancelledFlightMessage([]), { duration: 8000 });
                navigate(BOOKING_HISTORY_PATH, { replace: true });
              }
            }
          }
        }
      }
      setLoading(false);
    }
  };

  const getOriginalPaidAmount = (bookingData, breakdown) => {
    if (breakdown?.originalBooking?.total_amount != null) {
      return breakdown.originalBooking.total_amount;
    }
    const paid = parseFloat(
      bookingData?.total_paid_amount ?? bookingData?.paid_total_amount ?? 0
    );
    return Number.isFinite(paid) && paid > 0 ? paid : 0;
  };

  const getOriginalFlightsForChange = () =>
    originalBooking?.flights ||
    (Array.isArray(originalBooking?.formattedBookings)
      ? originalBooking.formattedBookings
      : null);

  const calculatePricing = () => {
    if (!flightData) return { farePerPax: 0, totalFare: 0, blockFare: 0, outboundFare: 0, returnFare: 0 };

    const originalFlightsForChange = getOriginalFlightsForChange();
    const changedLegsNewFare = calculateNewFareForChangedLegs({
      changeLegsQuery,
      originalFlights: originalFlightsForChange,
      flightData,
      returnFlightData,
      multiCityFlights,
      totalPassengers,
      isMultiCity,
      isReturnTrip,
    });

    // Multi-city: sum all flights
    if (isMultiCity && multiCityFlights && multiCityFlights.length > 0) {
      if (
        !originalBooking &&
        pricing &&
        (pricing.totalFare > 0 || pricing.blockFare > 0)
      ) {
        return {
          ...pricing,
          outboundFare: 0,
          returnFare: 0,
          isMultiCity: true
        };
      }

      const grandTotal = multiCityFlights.reduce(
        (sum, flight) => sum + getMultiCityFlightLegTotal(flight, totalPassengers),
        0
      );

      const result = { farePerPax: 0, totalFare: grandTotal, blockFare: 0, outboundFare: 0, returnFare: 0, isMultiCity: true };

      // Apply rebooking breakdown if this is a flight change
      if (originalBooking) {
        let originalPaid = getOriginalPaidAmount(originalBooking, changeFeeBreakdown);
        result.originalPaid = originalPaid;

        if (changeFeeBreakdown) {
          const breakdown = changeFeeBreakdown.priceBreakdown;
          // For multi-city, total_change_fee is the sum across all legs
          const totalChangeFee = changeFeeBreakdown.changeFeeCalculation?.total_change_fee || breakdown.changeFee || 0;
          result.changeFee = totalChangeFee;
          result.fareDifference = breakdown.fareDifference;
          result.payableDifference = breakdown.totalOwed;
          result.changeFeeLabel = `Change Fee (${multiCityFlights.length} flights)`;
        } else {
          result.changeFee = 0;
          result.fareDifference = changedLegsNewFare - originalPaid;
          result.payableDifference = Math.max(0, changedLegsNewFare - originalPaid);
        }
        result.totalFare = changedLegsNewFare;
      }

      return result;
    }

    // For return flights, always recalculate from fetched data.
    // For one-way NEW bookings, use pricing from URL params if available.
    // IMPORTANT: For one-way REBOOKINGS, we must continue and compute payableDifference.
    if (!isReturnTrip && !originalBooking && pricing && (pricing.farePerPax > 0 || pricing.totalFare > 0 || pricing.blockFare > 0)) {
      console.log('💰 Using pricing from URL params:', pricing);
      return { ...pricing, outboundFare: 0, returnFare: 0 };
    }

    const outboundPricing = calculateFlightPricing(flightData, totalPassengers);
    let farePerPax = outboundPricing.isSeatPricing ? outboundPricing.farePerPax : 0;
    let totalFare = outboundPricing.isSeatPricing ? outboundPricing.totalFare : 0;
    let blockFare = outboundPricing.blockFare;
    let outboundFare = calculateFlightLegTotal(flightData, totalPassengers);
    let returnFare = 0;

    if (returnFlightData) {
      returnFare = calculateFlightLegTotal(returnFlightData, totalPassengers);
      const returnPerPax = calculateFlightFarePerPax(returnFlightData);
      const outboundPerPax = calculateFlightFarePerPax(flightData);
      farePerPax = outboundPerPax + returnPerPax;
      totalFare = outboundFare + returnFare;
      blockFare = outboundPricing.blockFare + calculateFlightPricing(returnFlightData, totalPassengers).blockFare;
    }

    const result = { farePerPax, totalFare, blockFare, outboundFare, returnFare };

    if (originalBooking) {
      const originalPaid = getOriginalPaidAmount(originalBooking, changeFeeBreakdown);
      result.originalPaid = originalPaid;

      if (changeFeeBreakdown) {
        // Use backend-calculated breakdown: fare diff (floored at 0) + change fee
        const breakdown = changeFeeBreakdown.priceBreakdown;
        result.changeFee = breakdown.changeFee;
        result.fareDifference = breakdown.fareDifference;
        result.payableDifference = breakdown.totalOwed;
        result.changeFeeLabel = `Change Fee (${changeFeeBreakdown.changeFeeCalculation?.tier || ''})`;
      } else {
        // Fallback until breakdown loads: never credit itinerary amount on free bookings
        result.changeFee = 0;
        result.fareDifference = changedLegsNewFare - originalPaid;
        result.payableDifference = Math.max(0, changedLegsNewFare - originalPaid);
      }
      result.totalFare = changedLegsNewFare;
    }

    console.log('💰 Final pricing calculation:', result);
    return result;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Check JWT session validity before payment
  const checkSessionValidityBeforePayment = async (paymentMethod) => {
    // Skip check if we just refreshed the token (using ref for synchronous check)
    if (skipSessionCheckRef.current) {
      console.log('✅ [PaymentPage] Skipping session check (token just refreshed)');
      skipSessionCheckRef.current = false; // Reset immediately
      return true; // Return true immediately without checking
    }

    try {
      console.log('🔍 [PaymentPage] Checking session validity before payment...');
      
      // Log the token being sent
      const token = sessionStorage.getItem('token');
      console.log('🔑 [PaymentPage] Token from sessionStorage:', token ? `Present (length: ${token.length})` : 'Missing');
      if (token) {
        console.log('🔑 [PaymentPage] Token (first 50 chars):', token.substring(0, 50));
      }
      
      const response = await apiClient.get('/auth/session-validity');
      
      console.log('📊 [PaymentPage] Session validity response received');
      console.log('📊 [PaymentPage] Response object:', response);
      console.log('📊 [PaymentPage] Response.success:', response?.success);
      console.log('📊 [PaymentPage] Response.remaining_seconds:', response?.remaining_seconds);
      
      // Check if response has success flag
      if (response.success === false) {
        console.warn('⚠️ [PaymentPage] Session validity check failed:', response.message);
        navigate('/', { replace: true });
        showSessionExpiredNotice({ clearSession: true });
        return false;
      }

      // If response doesn't have success flag but has remaining_seconds, it's still valid
      if (!response.remaining_seconds && response.success !== true) {
        console.warn('⚠️ [PaymentPage] Invalid session response:', response);
        toast.error('Failed to verify session. Please try again.');
        return false;
      }

      setSessionValidity(response);
      
      // Check if remaining time is 15 minutes or less
      const remainingMinutes = response.remaining_time?.minutes || 0;
      const remainingSeconds = response.remaining_time?.seconds || 0;
      const totalRemainingSeconds = response.remaining_seconds || 0;
      
      console.log('⏱️ [PaymentPage] Session validity:', {
        remainingMinutes,
        remainingSeconds,
        totalRemainingSeconds,
        formatted: response.remaining_time?.formatted
      });

      if (totalRemainingSeconds <= 900) { // 15 minutes = 900 seconds
        console.log('⚠️ [PaymentPage] Session expiring soon (≤ 15 minutes). Showing warning modal.');
        setShowPaymentModal(false); // Hide payment method modal
        setShowSessionTimeoutWarning(true);
        setPendingPaymentMethod(paymentMethod);
        return false; // Don't proceed with payment yet
      }

      console.log('✅ [PaymentPage] Session valid for more than 15 minutes. Proceeding with payment.');
      return true; // Proceed with payment
    } catch (error) {
      console.error('❌ [PaymentPage] Error checking session validity:', error);
      console.error('❌ [PaymentPage] Error details:', {
        message: error.message,
        status: error.status,
        data: error.data
      });
      
      // If it's a 401, redirect to login
      if (error.status === 401) {
        navigate('/', { replace: true });
        showSessionExpiredNotice({ clearSession: true });
        return false;
      }
      
      toast.error('Failed to verify session. Please try again.');
      return false;
    }
  };

  // Handle staying signed in (refresh token and proceed with payment)
  const handleStaySignedInAndProceed = async () => {
    try {
      console.log('🔄 [PaymentPage] Token refreshed. Proceeding with payment...');
      
      // 1. Close modal FIRST, synchronously
      setShowSessionTimeoutWarning(false);
      setSessionValidity(null);
      
      // 2. Set skip flag BEFORE calling handlePaymentMethodSelect (using ref for synchronous update)
      skipSessionCheckRef.current = true;
      
      // 3. Now proceed with payment
      if (pendingPaymentMethod) {
        console.log('🔘 [PaymentPage] Resuming payment with method:', pendingPaymentMethod);
        handlePaymentMethodSelect(pendingPaymentMethod);
      }
    } catch (error) {
      console.error('❌ [PaymentPage] Error proceeding with payment:', error);
      toast.error('Failed to proceed with payment. Please try again.');
    }
  };

  // Handle logout from session timeout warning
  const handleLogoutFromWarning = () => {
    console.log('🚪 [PaymentPage] User logged out from session warning');
    setShowSessionTimeoutWarning(false);
    setPendingPaymentMethod(null);
    
    // Use existing logout utility
    import('../../utils/logout').then(({ performSimpleLogout }) => {
      performSimpleLogout();
    });
  };

  const formatDateTime = (datetime, format = 'time') => {
    const date = new Date(datetime);

    if (format === 'time') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } else if (format === 'date') {
      return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' });
    }
  };

  const handleAddressChange = (e) => {
    setAddress(e.target.value);
    // Clear error when user starts typing
    if (addressError) {
      setAddressError('');
    }
  };

  // ✅ Helper function to revoke seat lock on any failure
  const revokeSeatLockOnFailure = async (token, reason = 'unknown') => {
    const currentToken = token || bookingToken || bookingData.booking_token;
    if (!currentToken) {
      console.log('ℹ️ [revokeSeatLockOnFailure] No token to revoke');
      return;
    }

    console.log(`🔓 [revokeSeatLockOnFailure] Revoking seat lock for token ${currentToken} - Reason: ${reason}`);
    try {
      await bookingService.revokeLock(currentToken);
      console.log('✅ [revokeSeatLockOnFailure] Seat lock revoked successfully');
    } catch (err) {
      console.warn('⚠️ [revokeSeatLockOnFailure] Failed to revoke lock:', err);
    }
  };

  const ensureFlightsAvailableForPayment = async (flights, routeIds) => {
    const cancelled = findCancelledFlights(flights);
    if (cancelled.length > 0) {
      toast.error(buildCancelledFlightMessage(cancelled), { duration: 8000 });
      await revokeSeatLockOnFailure(null, 'flight cancelled');
      navigate(BOOKING_HISTORY_PATH, { replace: true });
      return false;
    }

    if (!routeIds?.length) {
      return true;
    }

    try {
      const paymentService = (await import('../../services/paymentService')).default;
      const verifyRes = await paymentService.verifyFlightsForPayment({ route_ids: routeIds });
      if (!verifyRes.success && isFlightCancelledApiResponse(verifyRes)) {
        toast.error(verifyRes.message || buildCancelledFlightMessage([]), { duration: 8000 });
        await revokeSeatLockOnFailure(null, 'flight cancelled (server)');
        navigate(BOOKING_HISTORY_PATH, { replace: true });
        return false;
      }
    } catch (verifyErr) {
      const payload = verifyErr.data || verifyErr;
      if (isFlightCancelledApiResponse(payload)) {
        toast.error(payload.message || buildCancelledFlightMessage([]), { duration: 8000 });
        await revokeSeatLockOnFailure(null, 'flight cancelled (server)');
        navigate(BOOKING_HISTORY_PATH, { replace: true });
        return false;
      }
    }

    return true;
  };

  const handleGoBack = () => {
    // ✅ Revoke seat lock before going back if token exists
    revokeSeatLockOnFailure(null, 'user clicked back');

    // Always clear change_flight when going back — user is abandoning the change flow
    apiClient.post('/payments/clear-change-flight')
      .then(() => console.log('✅ [Frontend] change_flight cleared on back'))
      .catch(err => console.warn('⚠️ [Frontend] Failed to clear change_flight:', err));

    // If inside a flow (onBack provided), call it
    if (onBack && typeof onBack === 'function') {
      onBack();
      return;
    }

    // Otherwise, standard navigation back to passengers page
    navigateWithBookingData(
      navigate,
      `/flight-booking-flow/${id}`,
      {
        ...bookingData,
        address,
        ...(paymentMethod ? { payment_method: paymentMethod } : {})
      }
    );
  };

  const handleConfirmRebooking = async () => {
    try {
      setProcessing(true);
      const tokenToUse = bookingToken || user.change_flight;
      
      if (!tokenToUse) {
        toast.error('No booking session found. Please try again.');
        // ✅ Revoke lock on failure
        await revokeSeatLockOnFailure(null, 'no booking session found');
        setProcessing(false);
        return;
      }

      const flightsForVerify = collectFlightsFromPaymentState({
        flightData,
        returnFlightData,
        multiCityFlights,
        bookingData,
      });
      const routeIdsForVerify = flightsForVerify.map((f) => f.id).filter(Boolean);
      const flightsOk = await ensureFlightsAvailableForPayment(flightsForVerify, routeIdsForVerify);
      if (!flightsOk) {
        setProcessing(false);
        return;
      }
      
      console.log('🔄 [handleConfirmRebooking] Confirming free rebooking with token:', tokenToUse);
      const response = await bookingService.confirmFreeRebooking(tokenToUse, routeIdsForVerify);

      if (response.success) {
        console.log('✅ [handleConfirmRebooking] Free rebooking successful');
        toast.success('Flight changed successfully!');
        // Small delay to ensure DB sync
        setTimeout(() => {
          navigate(`/booking-confirmation/${response.data.bookingToken || tokenToUse}`, { replace: true });
        }, 1500);
      } else {
        // ✅ NEW: Better error messages based on response + revoke lock
        const errorMsg = response.message || 'Failed to confirm flight change';
        console.error('❌ [handleConfirmRebooking] Backend error:', errorMsg);

        if (isFlightCancelledApiResponse(response)) {
          await revokeSeatLockOnFailure(null, 'flight cancelled');
          navigate(BOOKING_HISTORY_PATH, { replace: true });
          setProcessing(false);
          return;
        }
        
        // ✅ Revoke lock on failure
        await revokeSeatLockOnFailure(null, `confirm rebooking failed: ${errorMsg}`);
        
        if (errorMsg.includes('session') || errorMsg.includes('expired')) {
          toast.error('Your booking session has expired. Please start over.', { duration: 6000 });
          // Reset state to allow user to try again
          setNoPaymentRequired(false);
          setBookingToken(null);
        } else if (errorMsg.includes('No active flight change session')) {
          toast.error('No active flight change session. Please start over.', { duration: 6000 });
          setNoPaymentRequired(false);
          setBookingToken(null);
        } else {
          toast.error(errorMsg, { duration: 6000 });
        }
      }
    } catch (err) {
      console.error('❌ [handleConfirmRebooking] Error:', err);
      
      // ✅ NEW: Better error handling with fallback messages + revoke lock
      const errorMsg = err.data?.message || err.message || 'An error occurred while changing your flight.';
      console.error('❌ [handleConfirmRebooking] Exception:', errorMsg);

      if (isFlightCancelledApiResponse(err.data || err)) {
        await revokeSeatLockOnFailure(null, 'flight cancelled');
        navigate(BOOKING_HISTORY_PATH, { replace: true });
        setProcessing(false);
        return;
      }
      
      // ✅ Revoke lock on exception
      await revokeSeatLockOnFailure(null, `confirm rebooking exception: ${errorMsg}`);
      
      toast.error(errorMsg, { duration: 6000 });
      
      // Reset state to allow retry
      setNoPaymentRequired(false);
      setBookingToken(null);
    } finally {
      setProcessing(false);
    }
  };

  const handlePayNow = () => {
    // Validate address
    if (!address || address.trim() === '') {
      setAddressError('Please enter your address');
      // Scroll to the address field
      const addressElement = document.getElementById('address');
      if (addressElement) {
        addressElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        addressElement.focus();
      }
      return;
    }

    // Clear any previous error
    setAddressError('');

    // Show confirmation modal
    setShowConfirmModal(true);
  };

  const handleConfirmPayment = () => {
    setShowConfirmModal(false);
    const fromUrl = normalizePaymentMethod(bookingData.payment_method);
    if (fromUrl && !paymentMethod) {
      setPaymentMethod(fromUrl);
    }
    setShowPaymentModal(true);
  };

  const handlePaymentMethodSelect = async (method) => {
    console.log('🔘 [Frontend] handlePaymentMethodSelect called with method:', method);
    
    // ✅ NEW: Check session validity before proceeding
    const isSessionValid = await checkSessionValidityBeforePayment(method);
    if (!isSessionValid) {
      console.log('⚠️ [Frontend] Session validity check failed or session expiring soon. Stopping payment flow.');
      return;
    }

    setShowPaymentModal(false);
    setProcessing(true);

    try {
      // Validate that flight data is loaded
      if (!flightData) {
        console.error('❌ Flight data not loaded');
        // ✅ Revoke lock on failure
        await revokeSeatLockOnFailure(null, 'flight data not loaded');
        alert('Flight data is not loaded. Please refresh and try again.');
        setProcessing(false);
        return;
      }

      // --- NEW: LOCK SEATS HERE ---
      const pricingData = calculatePricing();
      
      // Determine total amount - use blockFare if it's a block booking, otherwise use totalFare
      let totalAmount = 0;
      if (pricingData.blockFare && pricingData.blockFare > 0) {
        totalAmount = pricingData.blockFare;
      } else if (pricingData.totalFare && pricingData.totalFare > 0) {
        totalAmount = pricingData.totalFare;
      }

      // Use payable difference for rebooking even when it is 0 (free rebooking).
      // Previous truthy-check skipped 0 and accidentally sent full new fare.
      if (originalBooking && pricingData.payableDifference !== undefined && pricingData.payableDifference !== null) {
        totalAmount = Number(pricingData.payableDifference);
      }

      console.log('💰 [Frontend] Payment calculation:', { totalAmount, blockFare: pricingData.blockFare, totalFare: pricingData.totalFare, payableDifference: pricingData.payableDifference, isRebooking: !!originalBooking });

      const isZeroAmountRebooking = !!originalBooking && Number(totalAmount) === 0;
      if ((isNaN(totalAmount) || totalAmount < 0 || (!isZeroAmountRebooking && totalAmount === 0))) {
        console.error('❌ Invalid amount:', { totalAmount, pricingData, flightData });
        toast.error('Invalid booking amount. Please refresh and try again.');
        setProcessing(false);
        return;
      }

      const flightsForVerify = collectFlightsFromPaymentState({
        flightData,
        returnFlightData,
        multiCityFlights,
        bookingData,
      });
      let routeIdsForVerify = flightsForVerify.map((f) => f.id).filter(Boolean);
      if (returnFlightData?.id && !routeIdsForVerify.includes(returnFlightData.id)) {
        routeIdsForVerify = [...routeIdsForVerify, returnFlightData.id];
      }

      const flightsAvailable = await ensureFlightsAvailableForPayment(
        flightsForVerify,
        routeIdsForVerify
      );
      if (!flightsAvailable) {
        setProcessing(false);
        return;
      }

      // CHECK FOR DUPLICATE BOOKINGS BEFORE PAYMENT
      console.log('🔍 [Frontend] Checking for duplicate bookings...');
      try {
        // Prepare flights array for duplicate check
        let flightsForCheck = [];
        
        if (routeType === 'Multicity' && multiCityFlights && multiCityFlights.length > 0) {
          flightsForCheck = multiCityFlights.map(flight => ({
            flight_number: flight.flight_number,
            departure_airport: flight.departure_airport,
            arrival_airport: flight.arrival_airport,
            departure_date_time: flight.departure?.datetime || flight.departure_date_time
          }));
        } else if ((routeType === 'Returns' || routeType === 'return') && flightData && returnFlightData) {
          flightsForCheck = [flightData, returnFlightData].map(flight => ({
            flight_number: flight.flight_number,
            departure_airport: flight.departure_airport,
            arrival_airport: flight.arrival_airport,
            departure_date_time: flight.departure?.datetime || flight.departure_date_time
          }));
        } else if (bookingData.selectedFlights && Array.isArray(bookingData.selectedFlights) && bookingData.selectedFlights.length > 1) {
          flightsForCheck = bookingData.selectedFlights.map(flight => ({
            flight_number: flight.flight_number,
            departure_airport: flight.departure_airport,
            arrival_airport: flight.arrival_airport,
            departure_date_time: flight.departure?.datetime || flight.departure_date_time
          }));
        } else if (flightData) {
          flightsForCheck = [{
            flight_number: flightData.flight_number,
            departure_airport: flightData.departure_airport,
            arrival_airport: flightData.arrival_airport,
            departure_date_time: flightData.departure?.datetime || flightData.departure_date_time
          }];
        }

        // Check for duplicates
        // Pass booking_token when rebooking so the backend excludes the original booking
        const duplicateCheckResponse = await bookingService.checkDuplicateBooking(
          passengers,
          flightsForCheck,
          bookingData.booking_token || null
        );
        
        if (!duplicateCheckResponse.success && duplicateCheckResponse.data?.duplicates) {
          console.warn('⚠️ [Frontend] Duplicate booking detected:', duplicateCheckResponse.data.duplicates);
          // ✅ Revoke lock on duplicate detection
          await revokeSeatLockOnFailure(null, 'duplicate booking detected');
          toast.error(`Cannot proceed: Passenger already booked on this flight.`, { duration: 8000 });
          setProcessing(false);
          return;
        }
        
        console.log('✅ [Frontend] No duplicate bookings found. Proceeding with payment...');
      } catch (err) {
        console.error('❌ [Frontend] Error checking duplicates:', err);
        // Check if error response contains duplicate info
        if (err.data?.duplicates) {
          console.warn('⚠️ [Frontend] Duplicate booking detected in error response:', err.data.duplicates);
          // ✅ Revoke lock on duplicate detection
          await revokeSeatLockOnFailure(null, 'duplicate booking detected in error');
          toast.error(`Cannot proceed: Passenger already booked on this flight.`, { duration: 8000 });
          setProcessing(false);
          return;
        }
        // Don't block payment if duplicate check fails for other reasons - log and continue
        console.warn('⚠️ [Frontend] Continuing despite duplicate check error');
      }

      // 1. Identify all flight legs
      let aircraftData = [];
      const currentRouteType = routeType === 'Returns' || routeType === 'return' ? 'return' : routeType === 'Multicity' ? 'multi_city' : 'one_way';

      if (currentRouteType === 'multi_city' && multiCityFlights && multiCityFlights.length > 0) {
        aircraftData = multiCityFlights.map(mapFlightToAircraftPayload);
      } else if (currentRouteType === 'return' && flightData && returnFlightData) {
        aircraftData = [flightData, returnFlightData].map(mapFlightToAircraftPayload);
      } else if (bookingData.selectedFlights && Array.isArray(bookingData.selectedFlights) && bookingData.selectedFlights.length > 1) {
        aircraftData = bookingData.selectedFlights.map(mapFlightToAircraftPayload);
      }

      const originalFlightsForChange =
        originalBooking?.flights ||
        (Array.isArray(originalBooking?.formattedBookings) ? originalBooking.formattedBookings : null);

      aircraftData = filterAircraftDataForPartialChange(
        aircraftData,
        changeLegsQuery,
        originalFlightsForChange,
        currentRouteType
      );

      const changeTokenCount = changeLegsQuery
        ? changeLegsQuery.split(',').map((t) => t.trim()).filter(Boolean).length
        : 0;
      const isPartialLegChange =
        aircraftData.length > 0 &&
        originalFlightsForChange?.length > 0 &&
        changeTokenCount > 0 &&
        changeTokenCount < originalFlightsForChange.length;

      const isMultiLeg =
        aircraftData.length > 1 ||
        (isPartialLegChange && currentRouteType !== 'one_way' && aircraftData.length > 0);
      const flightsToLock = aircraftData.length > 0 ? aircraftData : [flightData];
      const route_ids = flightsToLock.map(f => f.routes_id || f.id).filter(Boolean);

      // 2. Lock seats with comprehensive data
      const lockPayload = {
        ...bookingData,
        ...flightData,
        aircraftData: isMultiLeg ? aircraftData : undefined,
        passengers: passengers,
        passenger_count: totalPassengers,
        route_type: currentRouteType,
        amount: totalAmount,
        change_legs: changeLegsQuery
          ? changeLegsQuery.split(',').map((t) => t.trim()).filter(Boolean)
          : bookingData.change_legs,
      };

      console.log('🔒 [Frontend] Locking seats. IDs:', route_ids, 'Payload:', lockPayload);

      // For flight changes, pass the OLD booking_token so the backend can link it
      // For new bookings, pass null to create a new seat lock
      const seatLockToken = originalBooking ? bookingToken : null;

      const lockResponse = await bookingService.lockSeats(
        route_ids,
        totalPassengers,
        lockPayload,
        seatLockToken
      );

      if (!lockResponse.success) {
        console.error('❌ [Frontend] Failed to lock seats:', lockResponse.message);
        if (isFlightCancelledApiResponse(lockResponse)) {
          toast.error(lockResponse.message || buildCancelledFlightMessage([]), { duration: 8000 });
          await revokeSeatLockOnFailure(null, 'flight cancelled');
          navigate(BOOKING_HISTORY_PATH, { replace: true });
          setProcessing(false);
          return;
        }
        // ✅ Revoke lock on failure (though it may not have been locked)
        await revokeSeatLockOnFailure(null, `lock seats failed: ${lockResponse.message}`);
        toast.error(lockResponse.message || 'Failed to lock seats. They might have been taken.');
        setProcessing(false);
        return;
      }

      const newToken = lockResponse.data.booking_token;
      setBookingToken(newToken);
      sessionStorage.setItem('pending_booking_token', newToken);
      sessionStorage.setItem('pending_booking_time', Date.now().toString());

      // ⏰ START TIMER ONLY AFTER LOCK SEATS API SUCCEEDS
      console.log('⏰ [Frontend] Lock seats successful, starting timer now');
      setShowTimer(true);
      setTimeRemaining(600); // 10 minutes

      // 3. Prepare final payment data
      const resolvedNewFareAmount = changeFeeBreakdown?.priceBreakdown?.newFare
        ?? pricingData.totalFare
        ?? totalAmount;

      let paymentBookingData;
      if (isMultiLeg) {
        paymentBookingData = {
          aircraftData: aircraftData,
          selected_seat: totalPassengers,
          amount: totalAmount,
          new_fare_amount: resolvedNewFareAmount,
          passengers: passengers,
          address: address,
          route_type: currentRouteType,
          saveForFuture: bookingData.saveForFuture || false,
          flight_number: aircraftData[0].flight_number,
          departure_airport: aircraftData[0].departure_airport,
          departure_date_time: aircraftData[0].departure_date_time,
          arrival_airport: aircraftData[aircraftData.length - 1].arrival_airport,
          arrival_date_time: aircraftData[aircraftData.length - 1].arrival_date_time,
          change_legs: changeLegsQuery
          ? changeLegsQuery.split(',').map((t) => t.trim()).filter(Boolean)
          : bookingData.change_legs,
        };
      } else {
        paymentBookingData = {
          flight_id: flightData.id,
          aircraft_id: flightData.aircraft_id,
          registration_number: flightData.registration_number,
          flight_number: flightData.flight_number,
          departure_airport: flightData.departure_airport,
          departure_date_time: flightData.departure.datetime || flightData.departure_date_time,
          arrival_airport: flightData.arrival_airport,
          arrival_date_time: flightData.arrival.datetime || flightData.arrival_date_time,
          selected_seat: totalPassengers,
          amount: totalAmount,
          new_fare_amount: resolvedNewFareAmount,
          routes_id: flightData.id,
          passengers: passengers,
          address: address,
          route_type: 'one_way',
          saveForFuture: bookingData.saveForFuture || false,
          change_legs: changeLegsQuery
          ? changeLegsQuery.split(',').map((t) => t.trim()).filter(Boolean)
          : bookingData.change_legs,
        };
      }

      // Create payment session based on selected method
      if (method === 'stripe') {
        const paymentService = (await import('../../services/paymentService')).default;
        const response = await paymentService.createStripeSession(
          paymentBookingData,
          totalAmount,
          newToken, // Use the newly acquired token
          !!originalBooking // Pass rebooking flag
        );

        if (response.success) {
          // Handle no payment required case (same price rebooking)
          if (response.data.no_payment_required) {
            console.log('✅ No payment required - showing Create Free Booking button');
            setNoPaymentRequired(true);
            setBookingToken(response.data.booking_token);
            setProcessing(false);
            setShowPaymentModal(false);
            toast.success('Same price rebooking - click "Create Free Booking" to proceed', { duration: 4000 });
            return;
          } else if (response.data.url) {
            // Redirect to Stripe checkout
            window.location.href = response.data.url;
          } else {
            console.error('❌ [Frontend] Invalid Stripe response');
            // ✅ Revoke lock on invalid response
            await revokeSeatLockOnFailure(newToken, 'invalid stripe response');
            alert('Failed to create payment session: Invalid response');
            setProcessing(false);
          }
        } else {
          console.error('❌ [Frontend] Stripe session creation failed:', response.message);
          if (isFlightCancelledApiResponse(response)) {
            toast.error(response.message || buildCancelledFlightMessage([]), { duration: 8000 });
            await revokeSeatLockOnFailure(newToken, 'flight cancelled');
            navigate(BOOKING_HISTORY_PATH, { replace: true });
            setProcessing(false);
            return;
          }
          // ✅ Revoke lock on failure
          await revokeSeatLockOnFailure(newToken, `stripe session failed: ${response.message}`);
          alert('Failed to create payment session: ' + (response.message || 'Unknown error'));
          setProcessing(false);
        }
      } else if (method === 'paypal') {
        const paymentService = (await import('../../services/paymentService')).default;
        const response = await paymentService.createPayPalPayment(
          paymentBookingData,
          totalAmount,
          newToken, // Use the newly acquired token
          !!originalBooking // Pass rebooking flag
        );

        if (response.success) {
          // Handle no payment required case (same price rebooking)
          if (response.data.no_payment_required) {
            console.log('✅ No payment required - showing Create Free Booking button');
            setNoPaymentRequired(true);
            setBookingToken(response.data.booking_token);
            setProcessing(false);
            setShowPaymentModal(false);
            toast.success('Same price rebooking - click "Create Free Booking" to proceed', { duration: 4000 });
            return;
          } else if (response.data.approval_url) {
            // Redirect to PayPal checkout
            window.location.href = response.data.approval_url;
          } else {
            console.error('❌ [Frontend] Invalid PayPal response');
            // ✅ Revoke lock on invalid response
            await revokeSeatLockOnFailure(newToken, 'invalid paypal response');
            alert('Failed to create PayPal payment: Invalid response');
            setProcessing(false);
          }
        } else {
          console.error('❌ [Frontend] PayPal payment creation failed:', response.message);
          if (isFlightCancelledApiResponse(response)) {
            toast.error(response.message || buildCancelledFlightMessage([]), { duration: 8000 });
            await revokeSeatLockOnFailure(newToken, 'flight cancelled');
            navigate(BOOKING_HISTORY_PATH, { replace: true });
            setProcessing(false);
            return;
          }
          // ✅ Revoke lock on failure
          await revokeSeatLockOnFailure(newToken, `paypal payment failed: ${response.message}`);
          alert('Failed to create PayPal payment: ' + (response.message || 'Unknown error'));
          setProcessing(false);
        }
      } else {
        console.error('❌ [Frontend] Invalid payment method:', method);
        // ✅ Revoke lock on invalid method
        await revokeSeatLockOnFailure(newToken, `invalid payment method: ${method}`);
        alert('Invalid payment method selected');
        setProcessing(false);
      }
    } catch (err) {
      console.error('❌ [Frontend] Payment error:', err);
      // Use err.data?.message or err.message from our custom ApiClient
      const errorMsg = err.data?.message || err.message || 'Failed to process payment. Please try again.';

      if (isFlightCancelledApiResponse(err.data || err)) {
        toast.error(errorMsg, { duration: 8000 });
        await revokeSeatLockOnFailure(bookingToken, 'flight cancelled');
        navigate(BOOKING_HISTORY_PATH, { replace: true });
        setProcessing(false);
        return;
      }
      
      // ✅ Revoke lock on exception
      await revokeSeatLockOnFailure(bookingToken, `payment exception: ${errorMsg}`);
      
      toast.error(errorMsg, { duration: 6000 });
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-jet-gray-100 pt-4 pb-4 flex items-center justify-center">
        <img src="/assets/images/flight_logo.gif" alt="Loading..." className="w-24 h-24" />
      </div>
    );
  }

  if (error || !flightData) {
    return (
      <div className="min-h-screen bg-jet-gray-100 pt-4 pb-4">
        <div className="w-[86%] mx-auto py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Flight not found'}
          </div>
        </div>
      </div>
    );
  }

  const pricingData = calculatePricing();
  const totalAmount = pricingData.blockFare > 0 ? pricingData.blockFare : pricingData.totalFare;
  const rebookingFareDifference =
    changeFeeBreakdown?.priceBreakdown?.fareDifference ?? pricingData.fareDifference ?? 0;
  const hasAdditionalFareCost = rebookingFareDifference > 0;

  return (
    <div className="min-h-screen bg-jet-gray-100 pt-4 pb-4">
      {/* Session Expired Modal */}
      <SessionExpiredModal
        isOpen={showSessionModal}
        onClose={handleCloseModal}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Session Timeout Warning Modal */}
      <SessionTimeoutWarningModal
        isOpen={showSessionTimeoutWarning}
        remainingTime={sessionValidity}
        onStaySignedIn={handleStaySignedInAndProceed}
        onLogout={handleLogoutFromWarning}
      />

      {/* Main Content */}
      <div className="w-[86%] mx-auto py-8">
        {/* Outer White Wrapper */}
        <div className="bg-white rounded-lg shadow-jet p-6">
          <div className="space-y-6">
            {/* Combined Card - Flight Details, Fare Summary, Contact Details */}
            <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
              {/* Flight Details Section */}
              {isMultiCity && multiCityFlights.length > 0 ? (
                // Multi-City Flight Display - Show all flights
                <div className="p-6 space-y-6">
                  {multiCityFlights.map((flight, index) => (
                    <div key={flight.id} className={index < multiCityFlights.length - 1 ? "pb-6 border-b-2 border-dashed border-gray-300" : ""}>
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Aircraft Image */}
                        <div className="lg:col-span-2">
                          <div className="rounded-lg overflow-hidden h-32">
                            {flight.aircraft_images && flight.aircraft_images.length > 0 && !imageErrors[`multi-${flight.id}`] ? (
                              <img
                                src={getImageUrl(flight.aircraft_images[0].presigned_url || flight.aircraft_images[0].url)}
                                alt={flight.aircraft_images[0].name || 'Aircraft'}
                                className="w-full h-full object-cover"
                                onError={() => {
                                  setImageErrors(prev => ({ ...prev, [`multi-${flight.id}`]: true }));
                                }}
                              />
                            ) : (
                              <img
                                src="/assets/images/no_aircraft.png"
                                alt="Aircraft"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null; // Prevent infinite loop
                                }}
                              />
                            )}
                          </div>
                        </div>

                        {/* Flight Details */}
                        <div className="lg:col-span-10">
                          {/* Flight Number */}
                          <div className="mb-4">
                            <h4 className="text-base font-bold text-jet-text">
                              {flight.flight_number?.toUpperCase()}
                            </h4>
                          </div>

                          {/* Departure, Duration, Arrival */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Departure */}
                            <div>
                              <h4 className="text-sm font-bold text-jet-text mb-1">
                                {flight.departure.airport_code} {formatDateTime(flight.departure.datetime, 'time')}
                              </h4>
                              <p className="text-xs text-jet-text-light mb-2">
                                {formatDateTime(flight.departure.datetime, 'date')}
                              </p>
                            </div>

                            {/* Duration */}
                            <div className="text-center">
                              <p className="text-2xl text-jet-primary mb-1">→</p>
                              <p className="text-xs text-jet-text-light">
                                {formatFlightDurationLabel(flight)}
                              </p>
                            </div>

                            {/* Arrival */}
                            <div className="text-right">
                              <h4 className="text-sm font-bold text-jet-text mb-1">
                                {flight.arrival.airport_code} {formatDateTime(flight.arrival.datetime, 'time')}
                              </h4>
                              <p className="text-xs text-jet-text-light">
                                {formatDateTime(flight.arrival.datetime, 'date')}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : isReturnTrip && returnFlightData ? (
                // Return Flight Display - Show both flights
                <div className="p-6 space-y-6">
                  {/* Outbound Flight */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-6 border-b-2 border-dashed border-gray-300">
                    {/* Aircraft Image */}
                    <div className="lg:col-span-2">
                      <div className="rounded-lg overflow-hidden h-32">
                        {flightData.aircraft_images && flightData.aircraft_images.length > 0 && !imageErrors['outbound'] ? (
                          <img
                            src={getImageUrl(flightData.aircraft_images[0].presigned_url || flightData.aircraft_images[0].url)}
                            alt={flightData.aircraft_images[0].name || 'Aircraft'}
                            className="w-full h-full object-cover"
                            onError={() => {
                              setImageErrors(prev => ({ ...prev, outbound: true }));
                            }}
                          />
                        ) : (
                          <img
                            src="/assets/images/no_aircraft.png"
                            alt="Aircraft"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null; // Prevent infinite loop
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Flight Details */}
                    <div className="lg:col-span-10">
                      {/* Flight Number */}
                      <div className="mb-4">
                        <h4 className="text-base font-bold text-jet-text">
                          {flightData.flight_number?.toUpperCase()}
                        </h4>
                      </div>

                      {/* Departure, Duration, Arrival */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Departure */}
                        <div>
                          <h4 className="text-sm font-bold text-jet-text mb-1">
                            {flightData.departure.airport_code} {formatDateTime(flightData.departure.datetime, 'time')}
                          </h4>
                          <p className="text-xs text-jet-text-light mb-2">
                            {formatDateTime(flightData.departure.datetime, 'date')}
                          </p>
                        </div>

                        {/* Duration */}
                        <div className="text-center">
                          <p className="text-2xl text-jet-primary mb-1">→</p>
                          <p className="text-xs text-jet-text-light">
                            {formatFlightDurationLabel(flightData)}
                          </p>
                        </div>

                        {/* Arrival */}
                        <div className="text-right">
                          <h4 className="text-sm font-bold text-jet-text mb-1">
                            {flightData.arrival.airport_code} {formatDateTime(flightData.arrival.datetime, 'time')}
                          </h4>
                          <p className="text-xs text-jet-text-light">
                            {formatDateTime(flightData.arrival.datetime, 'date')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Return Flight */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Aircraft Image */}
                    <div className="lg:col-span-2">
                      <div className="rounded-lg overflow-hidden h-32">
                        {returnFlightData.aircraft_images && returnFlightData.aircraft_images.length > 0 && !imageErrors['return'] ? (
                          <img
                            src={getImageUrl(returnFlightData.aircraft_images[0].presigned_url || returnFlightData.aircraft_images[0].url)}
                            alt={returnFlightData.aircraft_images[0].name || 'Aircraft'}
                            className="w-full h-full object-cover"
                            onError={() => {
                              setImageErrors(prev => ({ ...prev, return: true }));
                            }}
                          />
                        ) : (
                          <img
                            src="/assets/images/no_aircraft.png"
                            alt="Aircraft"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null; // Prevent infinite loop
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Flight Details */}
                    <div className="lg:col-span-10">
                      {/* Flight Number */}
                      <div className="mb-4">
                        <h4 className="text-base font-bold text-jet-text">
                          {returnFlightData.flight_number?.toUpperCase()}
                        </h4>
                      </div>

                      {/* Departure, Duration, Arrival */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Departure */}
                        <div>
                          <h4 className="text-sm font-bold text-jet-text mb-1">
                            {returnFlightData.departure.airport_code} {formatDateTime(returnFlightData.departure.datetime, 'time')}
                          </h4>
                          <p className="text-xs text-jet-text-light mb-2">
                            {formatDateTime(returnFlightData.departure.datetime, 'date')}
                          </p>
                        </div>

                        {/* Duration */}
                        <div className="text-center">
                          <p className="text-2xl text-jet-primary mb-1">→</p>
                          <p className="text-xs text-jet-text-light">
                            {formatFlightDurationLabel(returnFlightData)}
                          </p>
                        </div>

                        {/* Arrival */}
                        <div className="text-right">
                          <h4 className="text-sm font-bold text-jet-text mb-1">
                            {returnFlightData.arrival.airport_code} {formatDateTime(returnFlightData.arrival.datetime, 'time')}
                          </h4>
                          <p className="text-xs text-jet-text-light">
                            {formatDateTime(returnFlightData.arrival.datetime, 'date')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // One-Way Flight Display (original)
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Aircraft Image */}
                    <div className="lg:col-span-2">
                      <div className="rounded-lg overflow-hidden h-32">
                        {flightData.aircraft_images && flightData.aircraft_images.length > 0 && !imageErrors['oneway'] ? (
                          <img
                            src={getImageUrl(flightData.aircraft_images[0].presigned_url || flightData.aircraft_images[0].url)}
                            alt={flightData.aircraft_images[0].name || 'Aircraft'}
                            className="w-full h-full object-cover"
                            onError={() => {
                              setImageErrors(prev => ({ ...prev, oneway: true }));
                            }}
                          />
                        ) : (
                          <img
                            src="/assets/images/no_aircraft.png"
                            alt="Aircraft"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null; // Prevent infinite loop
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Flight Details */}
                    <div className="lg:col-span-10">
                      {/* Flight Number */}
                      <div className="mb-4">
                        <h4 className="text-base font-bold text-jet-text">
                          {flightData.flight_number?.toUpperCase()}
                        </h4>
                      </div>

                      {/* Departure, Duration, Arrival */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Departure */}
                        <div>
                          <h4 className="text-sm font-bold text-jet-text mb-1">
                            {flightData.departure.airport_code} {formatDateTime(flightData.departure.datetime, 'time')}
                          </h4>
                          <p className="text-xs text-jet-text-light mb-2">
                            {formatDateTime(flightData.departure.datetime, 'date')}
                          </p>
                          {pricingData.blockFare > 0 ? (
                            <p className="text-xs font-bold text-jet-text">
                              Total Fare {getFormattedWithBoth(pricingData.blockFare)}
                            </p>
                          ) : (
                            <div className="flex gap-4 flex-wrap">
                              <p className="text-xs font-bold text-jet-text whitespace-nowrap">
                                Fare/Pax {getFormattedWithBoth(pricingData.farePerPax)}
                              </p>
                              <p className="text-xs font-bold text-jet-text whitespace-nowrap">
                                Total Fare {getFormattedWithBoth(pricingData.totalFare)}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Duration */}
                        <div className="text-center">
                          <p className="text-2xl text-jet-primary mb-1">→</p>
                          <p className="text-xs text-jet-text-light">
                            {formatFlightDurationLabel(flightData)}
                          </p>
                        </div>

                        {/* Arrival */}
                        <div className="text-right">
                          <h4 className="text-sm font-bold text-jet-text mb-1">
                            {flightData.arrival.airport_code} {formatDateTime(flightData.arrival.datetime, 'time')}
                          </h4>
                          <p className="text-xs text-jet-text-light">
                            {formatDateTime(flightData.arrival.datetime, 'date')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Fare Summary and Contact Details Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 p-[10px]">
                {/* Fare Summary */}
                <div className="p-[5px]">
                  <div className="bg-jet-yellow px-6 py-3 rounded-[5px]">
                    <h5 className="text-base font-bold text-jet-text">Fare Summary</h5>
                  </div>
                  <div className="p-6">
                    {isMultiCity && multiCityFlights && multiCityFlights.length > 0 ? (
                      // Multi-city fare breakdown
                      <div className="space-y-3">
                        {multiCityFlights.map((flight, idx) => {
                          const farePax = getMultiCityFlightFarePerPax(flight);
                          const legTotal = getMultiCityFlightLegTotal(flight, totalPassengers);
                          return (
                            <div key={idx} className="pb-3 border-b-2 border-dashed border-gray-300">
                              <p className="text-xs font-bold text-jet-text mb-2">
                                {flight.departure?.airport_code || 'DEP'} → {flight.arrival?.airport_code || 'ARR'}
                              </p>
                              <div className="flex justify-between items-center mb-1">
                                <p className="text-xs text-jet-text">Passenger(s)</p>
                                <p className="text-xs text-jet-text">{totalPassengers}</p>
                              </div>
                              <div className="flex justify-between items-center mb-1">
                                <p className="text-xs text-jet-text">Fare/Pax</p>
                                <p className="text-xs text-jet-text">{getFormattedWithBoth(farePax)}</p>
                              </div>
                              <div className="flex justify-between items-center">
                                <p className="text-xs font-bold text-jet-text">Total Fare</p>
                                <p className="text-xs font-bold text-jet-text">{getFormattedWithBoth(legTotal)}</p>
                              </div>
                            </div>
                          );
                        })}
                        <div className="pt-2">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-sm text-jet-text">Total Passenger(s)</p>
                            <b className="text-sm text-jet-text">{totalPassengers}</b>
                          </div>
                          {originalBooking ? (
                            <>
                              {/* Flight Change Breakdown for Multi-City */}
                              <div className="p-4 rounded-lg border-t border-gray-200 mt-2">
                                <h6 className="text-sm font-bold text-jet-text mb-3 border-t border-gray-200 pt-2 pb-2">
                                  ✈️ Flight Change Breakdown
                                </h6>

                                {/* Additional Cost — only when new fare exceeds original */}
                                {hasAdditionalFareCost && (
                                  <div className="mb-3 pb-3 border-b border-gray-300">
                                    <p className="text-xs text-gray-600 mb-1">Fare Difference</p>
                                    <div className="flex justify-between items-center">
                                      <p className="text-sm font-semibold text-jet-text">Additional Cost</p>
                                      <b className="text-sm text-red-600">
                                        +$ {formatCurrency(rebookingFareDifference)}
                                      </b>
                                    </div>
                                  </div>
                                )}

                                {/* Change Fee - per-flight breakdown for multi-city */}
                                <div className="mb-3 pb-3 border-b border-gray-300">
                                  <p className="text-xs text-gray-600 mb-1">Change Fee (per flight × passengers)</p>
                                  {changeFeeBreakdown?.changeFeeCalculation?.per_flight_breakdown ? (
                                    changeFeeBreakdown.changeFeeCalculation.per_flight_breakdown.map((leg, i) => (
                                      <div key={i} className="flex justify-between items-center mb-1">
                                        <p className="text-xs text-gray-500">
                                          {leg.flight_number} ({leg.fee_tier})
                                          {leg.passenger_count > 1
                                            ? ` · ${leg.passenger_count} passengers`
                                            : ''}
                                        </p>
                                        <b className="text-xs text-orange-600">+ $ {formatCurrency(leg.change_fee)}</b>
                                      </div>
                                    ))
                                  ) : null}
                                  <div className="flex justify-between items-center mt-1">
                                    <p className="text-sm font-semibold text-jet-text">Total Change Fee</p>
                                    <b className="text-sm text-orange-600">+ $ {formatCurrency(changeFeeBreakdown?.changeFeeCalculation?.total_change_fee || changeFeeBreakdown?.priceBreakdown?.changeFee || pricingData.changeFee || 0)}</b>
                                  </div>
                                </div>

                                {/* Total to Pay */}
                                <div className="bg-white p-3 rounded border-2 border-jet-primary">
                                  <div className="flex justify-between items-center">
                                    <p className="text-sm font-bold text-jet-text">Total Amount to Pay</p>
                                    {(changeFeeBreakdown?.priceBreakdown?.totalOwed || pricingData.payableDifference || 0) === 0 ? (
                                      <b className="text-base text-green-600 font-bold">✓ Nothing Extra to Pay</b>
                                    ) : (
                                      <b className="text-lg text-jet-primary font-bold">$ {formatCurrency(changeFeeBreakdown?.priceBreakdown?.totalOwed || pricingData.payableDifference || 0)}</b>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div>
                              <div className="flex justify-between items-center">
                                <p className="text-sm font-bold text-jet-text">Grand Total</p>
                                <b className="text-base text-jet-text">{getFormattedWithBoth(totalAmount)}</b>
                              </div>
                              {isLocalCurrency() && (
                                <div className="mt-3 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                                  <p className="text-xs text-gray-700">
                                    <strong>💳 Payment Currency:</strong> Your card will be charged in USD.
                                  </p>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {getDisclaimer()}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : isReturnTrip && returnFlightData ? (
                      // Return flight fare breakdown
                      <div className="space-y-3">
                        {/* Outbound Flight */}
                        <div className="pb-3 border-b-2 border-dashed border-gray-300">
                          <p className="text-xs font-bold text-jet-text mb-2">
                            {flightData.departure.airport_code} → {flightData.arrival.airport_code}
                          </p>
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-xs text-jet-text">Passengers</p>
                            <p className="text-xs text-jet-text">{totalPassengers}</p>
                          </div>
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-xs text-jet-text">Fare/Pax</p>
                            <p className="text-xs text-jet-text">
                              {getFormattedWithBoth(pricingData.outboundFare / totalPassengers)}
                            </p>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-xs font-bold text-jet-text">Total Fare</p>
                            <p className="text-xs font-bold text-jet-text">
                              {getFormattedWithBoth(pricingData.outboundFare)}
                            </p>
                          </div>
                        </div>

                        {/* Return Flight */}
                        <div className="pb-3 border-b-2 border-dashed border-gray-300">
                          <p className="text-xs font-bold text-jet-text mb-2">
                            {returnFlightData.departure.airport_code} → {returnFlightData.arrival.airport_code}
                          </p>
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-xs text-jet-text">Passengers</p>
                            <p className="text-xs text-jet-text">{totalPassengers}</p>
                          </div>
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-xs text-jet-text">Fare/Pax</p>
                            <p className="text-xs text-jet-text">
                              {getFormattedWithBoth(pricingData.returnFare / totalPassengers)}
                            </p>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-xs font-bold text-jet-text">Total Fare</p>
                            <p className="text-xs font-bold text-jet-text">
                              {getFormattedWithBoth(pricingData.returnFare)}
                            </p>
                          </div>
                        </div>

                        {/* Grand Total */}
                        <div className="pt-2">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-sm text-jet-text">Total Passenger(s)</p>
                            <b className="text-sm text-jet-text">{totalPassengers}</b>
                          </div>
                          {originalBooking ? (
                            <>
                              {/* Flight Change Breakdown */}
                              <div className="p-4 rounded-lg  border-t border-gray-200 mb-4">
                                <h6 className="text-sm font-bold text-jet-text mb-3 border-t border-gray-200 pt-2 pb-2">
                                  Flight Change Breakdown
                                </h6>

                                {/* Additional Cost — only when new fare exceeds original */}
                                {hasAdditionalFareCost && (
                                  <div className="mb-3 pb-3 border-b border-gray-300">
                                    <p className="text-xs text-gray-600 mb-1">Fare Difference</p>
                                    <div className="flex justify-between items-center">
                                      <p className="text-sm font-semibold text-jet-text">Additional Cost</p>
                                      <b className="text-sm text-red-600">
                                        +$ {formatCurrency(rebookingFareDifference)}
                                      </b>
                                    </div>
                                  </div>
                                )}

                                {/* Change Fee */}
                                {pricingData.changeFee > 0 && (
                                  <div className="mb-3 pb-3 border-b border-gray-300">
                                    <p className="text-xs text-gray-600 mb-1">{pricingData.changeFeeLabel || 'Change Fee'}</p>
                                    <div className="flex justify-between items-center">
                                      <p className="text-sm font-semibold text-jet-text">Change Fee</p>
                                      <b className="text-sm text-orange-600">+ $ {formatCurrency(pricingData.changeFee)}</b>
                                    </div>
                                  </div>
                                )}

                                {/* Total Calculation */}
                                <div className="bg-white p-3 rounded border-2 border-jet-primary">
                                  <div className="flex justify-between items-center">
                                    <p className="text-sm font-bold text-jet-text">Total Amount to Pay</p>
                                    {pricingData.payableDifference === 0 ? (
                                      <b className="text-base text-green-600 font-bold">✓ Nothing Extra to Pay</b>
                                    ) : (
                                      <b className="text-lg text-jet-primary font-bold">$ {formatCurrency(pricingData.payableDifference || 0)}</b>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div>
                              <div className="flex justify-between items-center">
                                <p className="text-sm font-bold text-jet-text">Total Fare</p>
                                <b className="text-base text-jet-text">{getFormattedWithBoth(totalAmount)}</b>
                              </div>
                              {isLocalCurrency() && (
                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                  <p className="text-xs font-medium text-blue-900">
                                    💳 Your card will be charged in USD
                                  </p>
                                  <p className="text-xs text-blue-700 mt-1">
                                    {getDisclaimer()}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      // One-way flight fare (original)
                      <div className="space-y-3">
                        {originalBooking ? (
                          <>
                            {/* Flight Change Breakdown */}
                            <div className="p-4 rounded-lg border-b border-gray-300 mb-4">
                              <h6 className="text-sm font-bold text-jet-text mb-3 pb-2 border-b-2 border-gray-300">
                                ✈️ Flight Change Breakdown
                              </h6>
                              
                              {/* Additional Cost — only when new fare exceeds original */}
                              {hasAdditionalFareCost && (
                                <div className="mb-3 pb-3 border-b border-gray-300">
                                  <p className="text-xs text-gray-600 mb-1">Fare Difference</p>
                                  <div className="flex justify-between items-center">
                                    <p className="text-sm font-semibold text-jet-text">Additional Cost</p>
                                    <b className="text-sm text-red-600">
                                      +$ {formatCurrency(rebookingFareDifference)}
                                    </b>
                                  </div>
                                </div>
                              )}

                              {/* Change Fee */}
                              <div className="mb-3 pb-3 border-b border-gray-300">
                                <p className="text-xs text-gray-600 mb-1">{changeFeeBreakdown?.changeFeeCalculation?.tier ? `Change Fee (${changeFeeBreakdown.changeFeeCalculation.tier})` : 'Change Fee'}</p>
                                <div className="flex justify-between items-center">
                                  <p className="text-sm font-semibold text-jet-text">Change Fee</p>
                                  <b className="text-sm text-orange-600">+ $ {formatCurrency(changeFeeBreakdown?.priceBreakdown?.changeFee || pricingData.changeFee || 0)}</b>
                                </div>
                              </div>

                              {/* Total Calculation */}
                              <div className="bg-white p-3 rounded border-2 border-jet-primary">
                                <div className="flex justify-between items-center">
                                  <p className="text-sm font-bold text-jet-text">Total Amount to Pay</p>
                                  {(changeFeeBreakdown?.priceBreakdown?.totalOwed || pricingData.payableDifference || 0) === 0 ? (
                                    <b className="text-base text-green-600 font-bold">✓ Nothing Extra to Pay</b>
                                  ) : (
                                    <b className="text-lg text-jet-primary font-bold">$ {formatCurrency(changeFeeBreakdown?.priceBreakdown?.totalOwed || pricingData.payableDifference || 0)}</b>
                                  )}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex justify-between items-center border-b-2 border-dotted border-gray-200">
                              <p className="text-sm text-jet-text">Total Passenger(s)</p>
                              <b className="text-sm text-jet-text">{totalPassengers}</b>
                            </div>
                            <div>
                              <div className="flex justify-between items-center pt-2">
                                <p className="text-sm text-jet-text">Total Fare</p>
                                <b className="text-base text-jet-text">{getFormattedWithBoth(totalAmount)}</b>
                              </div>
                              {isLocalCurrency() && (
                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                  <p className="text-xs font-medium text-blue-900">
                                    💳 Your card will be charged in USD
                                  </p>
                                  <p className="text-xs text-blue-700 mt-1">
                                    {getDisclaimer()}
                                  </p>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Details */}
                <div className="p-[5px]">
                  <div className="bg-jet-yellow px-6 py-3 rounded-[5px]">
                    <h5 className="text-base font-bold text-jet-text">Contact Details</h5>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start border-b-2 border-dotted border-gray-200">
                        <p className="text-sm text-jet-text">Name</p>
                        <p className="text-sm font-semibold text-jet-text uppercase text-right">
                          {user?.first_name} {user?.last_name}
                        </p>
                      </div>
                      <div className="flex justify-between items-start border-b-2 border-dotted border-gray-200">
                        <p className="text-sm text-jet-text">Email</p>
                        <p className="text-sm font-semibold text-jet-text uppercase text-right">
                          {user?.email}
                        </p>
                      </div>
                      <div className="flex justify-between items-start border-b-2 border-dotted border-gray-200">
                        <p className="text-sm text-jet-text">Phone Number :</p>
                        <p className="text-sm font-semibold text-jet-text text-right">
                          {passengers[0]?.country_code || user?.phone_code} {passengers[0]?.contact_number || user?.mobile_number}
                        </p>
                      </div>
                      <div className="flex justify-between items-start">
                        <p className="text-sm text-jet-text">Address :</p>
                        {user?.address ? (
                          <p className="text-sm font-semibold text-jet-text uppercase text-right flex-1 ml-4">
                            {user.address}
                          </p>
                        ) : (
                          <div className="flex-1 ml-4">
                            <textarea
                              id="address"
                              name="address"
                              className={`w-full px-3 py-2 border rounded text-sm uppercase focus:outline-none focus:ring-2 resize-none ${addressError
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-jet-border focus:ring-jet-primary'
                                }`}
                              value={address}
                              onChange={handleAddressChange}
                              placeholder="Enter Your Address..."
                              rows="2"
                            />
                            {addressError && (
                              <p className="text-red-500 text-xs mt-1">
                                {addressError}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Button */}
              <div className="flex flex-col items-center gap-3 py-6">
                {/* Booking Countdown Timer */}
                {showTimer && timeRemaining !== null && (
                  <div className="w-full px-6">
                    <div style={{
                      padding: '12px',
                      background: '#fffbf0',
                      borderLeft: '4px solid #ffd200',
                      borderRadius: '4px',
                    }}>
                      <div className="flex items-center gap-2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffd200" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        <span style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>
                          Complete payment within{' '}
                          <strong style={{
                            color: getTimerColor(),
                            fontSize: '15px',
                            fontFamily: "'Courier New', monospace",
                            fontWeight: '700'
                          }}>
                            {formatTime(timeRemaining)}
                          </strong>
                          {' '}to secure your booking
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-center gap-4">
                  <button
                    type="button"
                    className="px-8 py-2 bg-gray-300 text-jet-text font-bold rounded hover:bg-gray-400 transition-colors shadow-md text-sm"
                    onClick={handleGoBack}
                  >
                    Back
                  </button>
                  {noPaymentRequired ? (
                    <button
                      type="button"
                      className="px-8 py-2 bg-green-600 text-white font-bold rounded hover:bg-green-700 transition-colors shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                      onClick={handleConfirmRebooking}
                      disabled={processing}
                    >
                      {processing ? 'Processing...' : 'Create Free Booking'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="px-8 py-2 bg-jet-yellow text-jet-text font-bold rounded hover:bg-yellow-500 transition-colors shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                      onClick={handlePayNow}
                      disabled={processing || loading || (showTimer && timeRemaining === 0)}
                    >
                      {processing ? 'Processing...' : loading ? 'Loading...' : 'Pay Now'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <UniversalConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmPayment}
        type="warning"
        title="Are you sure?"
        message="Please check and confirm that all passenger(s) information are entered correctly before proceeding to avoid additional charges. Click Ok to proceed or Cancel to exit."
        confirmText="OK"
        cancelText="CANCEL"
        showCancelButton={true}
        theme="modern"
      />

      {/* Payment Method Modal */}
      {showPaymentModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-[30rem] w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Yellow Header */}
            <div className="border-t-8 border-yellow-400 px-8 py-4">
              <h5 className="text-2xl font-bold text-gray-800 m-0 text-center">Select Payment Method</h5>
            </div>

            {/* Content */}
            <div className="p-[10px]">
              <div className="grid grid-cols-2 mb-4 gap-0">
                {/* Stripe Option */}
                <div
                  className="flex flex-col items-center cursor-pointer"
                  onClick={() => selectPaymentMethod('stripe')}
                >
                  <div className="bg-gray-300 rounded-lg p-4 w-[75%] flex flex-col items-center justify-center min-h-40">
                    <div className="mb-4">
                      <input
                        type="radio"
                        name="payment_method"
                        value="stripe"
                        checked={paymentMethod === 'stripe'}
                        onChange={() => selectPaymentMethod('stripe')}
                        className="w-6 h-6 cursor-pointer"
                      />
                    </div>
                    <img src="/stripe_logo.png" alt="Stripe" className="max-w-full h-12 object-contain" />
                  </div>
                </div>

                {/* PayPal Option */}
                <div
                  className="flex flex-col items-center cursor-pointer"
                  onClick={() => selectPaymentMethod('paypal')}
                >
                  <div className="bg-gray-300 rounded-lg w-[75%] flex flex-col items-center justify-center min-h-40">
                    <div className="mb-4">
                      <input
                        type="radio"
                        name="payment_method"
                        value="paypal"
                        checked={paymentMethod === 'paypal'}
                        onChange={() => selectPaymentMethod('paypal')}
                        className="w-6 h-6 cursor-pointer"
                      />
                    </div>
                    <img src="/PayPal-Logo.png" alt="PayPal" className="max-w-full h-16 object-contain" />
                  </div>
                </div>
              </div>

              {paymentMethod === 'stripe' && (
                <div className="mb-4 px-4 border-t border-gray-100 pt-3">
                  {renderAvailablePromoCodes()}
                  {availablePromos.length > 0 && (
                    <p className="text-xs text-gray-500 text-center m-0 mb-2">
                      Tap a code to copy it, then paste it into &quot;Add promotion code&quot; on the Stripe page.
                    </p>
                  )}
                </div>
              )}
              {paymentMethod === 'paypal' && (
                <p className="text-xs text-gray-500 text-center mx-4 mb-4 mt-0 border-t border-gray-100 pt-3">
                  Promotion codes apply to Stripe checkout only.
                </p>
              )}

              {/* Buttons */}
              <div className="flex justify-center gap-4">
                <button
                  type="button"
                  className="px-12 py-3 bg-yellow-400 text-gray-800 font-bold rounded hover:bg-yellow-500 transition-colors"
                  onClick={() => {
                    if (paymentMethod) {
                      handlePaymentMethodSelect(paymentMethod);
                    }
                  }}
                >
                  OK
                </button>
                <button
                  type="button"
                  className="px-12 py-3 border-2 border-gray-400 text-gray-800 font-bold rounded hover:border-gray-600 transition-colors bg-white"
                  onClick={() => setShowPaymentModal(false)}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;

